import { z } from "zod";
import { localDateSchema, moneySchema, uuidSchema } from "../common/primitives.js";
import { evPaymentStatusSchema } from "./enums.js";
/**
 * Şarj raporları (Prompt 19, ADR 0019 §15–16) — izin `ev.report.read`, istasyon kapsamlı, bayrak `EV`. Tarih aralığı
 * oturumun BAŞLADIĞI İstanbul günüdür (`local_day`), dahil, en çok 366 gün. Para kuruş dizgesi; kWh 3 ondalık dizge.
 * Formüller `docs/domain/ev.md` §Raporlar'dadır.
 */
const rangeQuery = {
    stationId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
};
const refineRange = (value, context) => {
    if (value.from > value.to)
        context.addIssue({ code: "custom", path: ["to"], message: "Bitiş başlangıçtan önce olamaz" });
    if ((Date.parse(value.to) - Date.parse(value.from)) / 86_400_000 > 366)
        context.addIssue({ code: "custom", path: ["to"], message: "Aralık en çok 366 gün olabilir" });
};
export const evReportQuerySchema = z.object(rangeQuery).superRefine(refineRange);
// ── Enerji / maliyet / marj ──────────────────────────────────────────────────
export const EV_ENERGY_COST_SOURCES = ["EV_METER", "STATION_METER", "NONE"];
const energyRow = {
    sessionCount: z.number().int(),
    energyKwh: z.string(),
    revenueNet: moneySchema.describe("Ödenen (kasa) / faturalanan oturumların KDV HARİÇ tutarı"),
    energyCost: moneySchema.describe("Oturum kWh × birim maliyet (rapor hesabı; fiş yok)"),
    margin: moneySchema,
};
export const evEnergyReportSchema = z.object({
    stationId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    totals: z.object({
        ...energyRow,
        completedCount: z.number().int(),
        coveredKwh: z.string().describe("Abonelik hakkıyla karşılanan kWh"),
        revenueGross: moneySchema.describe("Ödenen / faturalanan oturumların KDV DAHİL tutarı"),
        partnerAmount: moneySchema.describe("Şarj ağı işletmecisinin oturumları (KDV dahil; istasyon geliri değil, #76)"),
        unpaidAmount: moneySchema.describe("Tamamlanmış, ödenmemiş oturumlar (KDV dahil)"),
        marginPercent: z
            .string()
            .nullable()
            .describe("Marj / KDV hariç gelir × 100 (2 ondalık); gelir 0 ise null"),
        avgSessionMinutes: z
            .string()
            .nullable()
            .describe("Tamamlanan OCPP oturumlarında başlangıç → araç çekildi"),
        avgChargingMinutes: z.string().nullable(),
        avgIdleMinutes: z.string().nullable().describe("Şarj bitti → araç çekildi (bekleme)"),
    }),
    energyCost: z.object({
        source: z.enum(EV_ENERGY_COST_SOURCES),
        meterId: uuidSchema.nullable(),
        meterName: z.string().nullable(),
        unitCost: z.string().nullable().describe("₺ / kWh (onaylı sayaç faturalarından, 4 ondalık)"),
        meterConsumptionKwh: z
            .string()
            .nullable()
            .describe("EV alt sayacının dönem tüketimi (okuma farkı); alt sayaç / okuma yoksa null"),
        sessionEnergyKwh: z.string().describe("OCPP oturumlarının kWh toplamı (şarj ağı oturumları hariç)"),
        lossKwh: z.string().nullable().describe("Sayaç tüketimi − oturum toplamı (kayıp); alt sayaç yoksa null"),
        lossPercent: z.string().nullable(),
    }),
    byCharger: z.array(z.object({ chargerId: uuidSchema.nullable(), chargerName: z.string(), ...energyRow })),
    byDay: z.array(z.object({ date: localDateSchema, ...energyRow })),
    byPaymentStatus: z.array(z.object({
        paymentStatus: evPaymentStatusSchema,
        sessionCount: z.number().int(),
        energyKwh: z.string(),
        totalAmount: moneySchema,
    })),
    warnings: z.array(z.string()),
});
// ── Doluluk ──────────────────────────────────────────────────────────────────
export const evOccupancyReportSchema = z.object({
    stationId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    connectorCount: z.number().int(),
    /** [hafta günü 0 = Pazartesi … 6 = Pazar][saat 0–23] → dolu geçen saniye (İstanbul saati; tüm konnektörler). */
    seconds: z.array(z.array(z.number().int())),
    /** Hücre doluluk yüzdesi: saniye / (hücrenin dönemdeki saat sayısı × 3600 × konnektör sayısı), 1 ondalık. */
    percent: z.array(z.array(z.string())),
    byConnector: z.array(z.object({
        chargerId: uuidSchema,
        chargerName: z.string(),
        connectorNo: z.number().int(),
        occupiedSeconds: z.number().int(),
        occupancyPercent: z.string().describe("Dolu / dönemin toplam süresi × 100 (1 ondalık)"),
    })),
});
// ── Erişilemezlik / arıza ────────────────────────────────────────────────────
const bucketSeconds = z.object({
    AVAILABLE: z.number().int(),
    OCCUPIED: z.number().int(),
    RESERVED: z.number().int(),
    FAULTED: z.number().int(),
    UNAVAILABLE: z.number().int(),
    OFFLINE: z.number().int(),
});
export const evAvailabilityReportSchema = z.object({
    stationId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    totals: z.object({
        downtimeSeconds: z
            .number()
            .int()
            .describe("Arıza + kullanım dışı + bağlantı kopukluğu (konnektör saniyesi)"),
        faultSeconds: z.number().int(),
        offlineSeconds: z.number().int().describe("Ünite bazında bağlantı kopukluğu (sn)"),
        faultCount: z.number().int(),
    }),
    connectors: z.array(z.object({
        chargerId: uuidSchema,
        chargerName: z.string(),
        connectorNo: z.number().int(),
        seconds: bucketSeconds,
        downtimeSeconds: z.number().int(),
        availabilityPercent: z
            .string()
            .nullable()
            .describe("(izlenen − erişilemez) / izlenen × 100 (1 ondalık); durum kaydı yoksa null"),
    })),
    chargers: z.array(z.object({
        chargerId: uuidSchema,
        chargerName: z.string(),
        offlineSeconds: z.number().int(),
        disconnectCount: z.number().int(),
        neverConnected: z.boolean(),
    })),
    faults: z.array(z.object({
        chargerId: uuidSchema,
        chargerName: z.string(),
        connectorNo: z.number().int().describe("0 = ünitenin kendisi"),
        errorCode: z.string(),
        seconds: z.number().int(),
        count: z.number().int(),
    })),
});
// ── Şarj müşterisinin market / kafe harcaması ────────────────────────────────
const spendAmounts = {
    sessionCount: z.number().int(),
    energyKwh: z.string(),
    evAmount: moneySchema.describe("Oturumların tutarı (KDV dahil)"),
    duringSessionAmount: moneySchema.describe("Oturum sırasında (başlangıç → araç çekildi) kasa harcaması"),
    sameDayAmount: moneySchema.describe("Oturumun günü o istasyondaki kasa harcaması (oturum sırasındakiler dahil)"),
    marketAmount: moneySchema,
    cafeAmount: moneySchema,
    otherAmount: moneySchema.describe("Market / kafe dışı (akaryakıt, yıkama …) — şarj satırları hariç"),
};
export const evCustomerSpendReportSchema = z.object({
    stationId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    totals: z.object({ customerCount: z.number().int(), ...spendAmounts }),
    customers: z.array(z.object({
        customerProfileId: uuidSchema,
        customerCode: z.string(),
        customerName: z.string().nullable().describe("KVKK: `customers.personal.view` yoksa maskeli"),
        ...spendAmounts,
    })),
});
//# sourceMappingURL=reports.js.map